import { type FuseResult } from 'fuse.js';
import { type ChangeEvent, memo, useCallback, useMemo, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { Modal, ModalCloseButton, ModalTitle } from '@air/primitive-modal';
import { isAdminDotAirDotInc } from '@air/sysadmin';
import { type FullWorkspace } from '@air/types';

import { InternalBrowseWorkspaceModalList } from '~/components/v2/components/internal/modals/internal-browse-workspace-modal/InternalBrowseWorkspaceModalList';
import { InternalBrowseWorkspaceModalSearch } from '~/components/v2/components/internal/modals/internal-browse-workspace-modal/InternalBrowseWorkspaceModalSearch';
import {
  ScrollElementContextConsumer,
  ScrollElementContextProvider,
} from '~/components/v2/components/shared/scroll/ScrollElementContextProvider';
import { useWorkspaces } from '~/swr-hooks/workspaces/useWorkspaces';

export type InternalBrowseWorkspaceModalProps = unknown;

export const InternalBrowseWorkspaceModal = memo(({ onClose }: AirModalProps<InternalBrowseWorkspaceModalProps>) => {
  const { data: workspaces, isLoading } = useWorkspaces(undefined, {
    /**
     * We will be disabling this on admin.air.inc to prevent querying every single workspace ever created.
     * @TODO - Remove this once BE adds in `limit`
     * @link https://linear.app/air/issue/ENG-142/improve-browse-workspace-modal
     */
    enabled: !isAdminDotAirDotInc(),
  });
  const [fuseResults, setFuseResults] = useState<FuseResult<FullWorkspace>[]>([]);

  const [debouncedSearch] = useDebouncedCallback(async (value: string) => {
    const Fuse = (await import('fuse.js')).default;
    const fuse = new Fuse(workspaces ?? [], {
      includeScore: true,
      keys: ['name', 'plan', 'id'],
    });

    setFuseResults(fuse.search(value));
  }, 200);

  const onSearch = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      debouncedSearch(event.target.value);
    },
    [debouncedSearch],
  );

  const workspacesCount = useMemo(() => {
    return workspaces?.length ?? 0;
  }, [workspaces]);

  const flatWorkspaces = useMemo(() => {
    /**
     * @TODO - Remove this once BE adds fuzzysearch on the Workspaces.list endpoint
     * @link https://linear.app/air/issue/ENG-142/improve-browse-workspace-modal
     */
    if (fuseResults.length > 0) {
      return fuseResults.map((workspace) => workspace.item);
    }

    return workspaces ?? [];
  }, [fuseResults, workspaces]);

  return (
    <Modal className="flex max-h-[calc(100vh-10vw-32px)] flex-col p-0" isOpen onDismiss={onClose} size="large">
      <header className="flex shrink-0 flex-col gap-4 border-b border-b-grey-5 p-6">
        <div className="flex items-center justify-between gap-2">
          <ModalTitle>Browse workspaces ({workspacesCount})</ModalTitle>
          <ModalCloseButton onClick={onClose} />
        </div>
        <InternalBrowseWorkspaceModalSearch onChange={onSearch} />
      </header>
      <ScrollElementContextProvider>
        <ScrollElementContextConsumer>
          {({ setScrollElement }) => (
            <section
              aria-live="polite"
              aria-busy={isLoading}
              className="flex flex-col overflow-y-auto"
              ref={setScrollElement}
            >
              <InternalBrowseWorkspaceModalList onSwitchWorkspace={onClose} workspaces={flatWorkspaces ?? []} />
            </section>
          )}
        </ScrollElementContextConsumer>
      </ScrollElementContextProvider>
    </Modal>
  );
});

InternalBrowseWorkspaceModal.displayName = 'InternalBrowseWorkspaceModal';
